/** @jsx jsx */
import {
  jsx,
  Box,
  Button,
  Grid,
  Spinner,
  Container,
  useThemeUI,
} from "theme-ui";
import { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import submitDonationRequest from "sites-common/service-wrappers/donation-service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePersist } from "../../../globals";
import Tos from "../../Tos";
import CurrencySymbol from "../../ui/CurrencySymbol";
import validateProfile from "./DonorProfile/validateProfile";
import AmountSelector from "./Amount/AmountSelector";
import ProfileSelector from "./DonorProfile/ProfileSelector";

import ErrorsOrWarnings from "./ErrorsOrWarnings";
import EditProfile from "./DonorProfile/EditProfile";
import useDonorProfiles from "../hooks/useDonorProfiles";
import { checkRestrictions } from "./restrictions";
import useDonationConf from "../hooks/useDonationConf";
import ErrorsOrWarningsWrapper from "./ErrorsOrWarningsWrapper";
import RestrictionsWrapper from "./RestrictionsWrapper";

const getMandatoryEnv = require("sites-common/utils/getMandatoryEnv");

const { donationServiceConfig } = getMandatoryEnv(["donationServiceConfig"]);

const doValidateSubmit = ({
  country,
  systemInfo,
  donorInfo,
  amount,
  currency,
  onErrors,
  onWarnings,
  setLoading,
  onSuccess,
  overrideWarnings = [],
  conf,
}) => {
  const { errors, warnings } = validateProfile(
    country,
    donorInfo,
    amount,
    currency,
    false
  );
  if (errors.length) {
    // console.error(errors);
    onErrors(errors);
    onWarnings(warnings);
    return;
  }

  if (warnings) {
    const pendingWarnings = warnings.filter(
      (w) => !overrideWarnings.includes(w)
    );
    if (pendingWarnings.length) {
      // console.warn(warnings);
      onWarnings(pendingWarnings);
      return;
    }
  }

  const restrictions = checkRestrictions({
    conf,
    country,
    amount,
    donorInfo,
    donationId: systemInfo.donation_id,
  });

  if (restrictions.length) {
    return;
  }

  const formData = {
    donationServiceConfig,
    ...systemInfo,
    ...donorInfo,
    currency,
    amount,
  };

  submitDonationRequest(
    formData,
    setLoading,
    onSuccess,
    (s) => onErrors([s]),
    conf
  );
};

const OnlineWidget = ({
  formDonationId,
  initialAmount,
  initialCurrency,
  projectId,
  packageDescription,
  country,
  initialProfile,
  paymentSuccessUrl,
  paymentFailureUrl,
  showOtherCitizen,
  enableSignInOption,
  meditationPlaceFieldAvl,
}) => {
  const { theme } = useThemeUI();
  const [to] = useState(initialCurrency);
  const [toAmount, setToAmount] = useState(initialAmount);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [donorInfo, setDonorInfo] = useState({});

  const [loading, setLoading] = useState(false);

  const [editDonorField, setEditDonorField] = useState(false);
  const { numProfiles } = useDonorProfiles(country);

  const { conf } = useDonationConf();

  const [trk, setTrk] = usePersist("donation-trk", {});
  const onSuccess = useCallback(
    (d) => {
      setTrk({
        ...trk,
        [d.donation.payment.trackingId]: d.donation.userEmailAddress,
      });
    },
    [trk, setTrk]
  );

  const containerStyle = {
    mb: 1,
    borderBottom: `thin ${theme.colors.primary} solid`,
  };

  useEffect(() => {
    // Fix for scroll down while focusing on form fields in mobile (IOS and Android)
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const systemInfo = useMemo(
    () => ({
      donation_id: formDonationId,
      projects: [projectId],
      packageInfo: packageDescription,
      paymentSuccessUrl,
      paymentFailureUrl,
    }),
    [formDonationId, projectId, packageDescription]
  );

  const resetDonorInfo = useCallback(() => {
    setDonorInfo(initialProfile);
    setErrors([]);
    setWarnings([]);
  }, [initialProfile]);

  const setDonorInfoAndTriggerPay = useCallback(
    (p) => {
      setDonorInfo(p);
      setEditDonorField(false);

      if (p) {
        doValidateSubmit({
          donationServiceConfig,
          country,
          systemInfo,
          donorInfo: p,
          amount: toAmount,
          currency: to,
          onErrors: setErrors,
          onWarnings: setWarnings,
          setLoading,
          onSuccess,
          overrideWarnings: [],
          conf,
        });
      }
    },
    [toAmount, to, country, onSuccess, systemInfo]
  );

  const validateAndSubmit = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      overrideWarnings: [],
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    country,
    systemInfo,
  ]);

  const validateAndSubmitOW = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      overrideWarnings: ["warning-in-pan", "warning-in-identity"],
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    country,
    systemInfo,
  ]);

  // profileModes = ["restrictions", "selection-box", "editor-box", "warnings-box"];

  const restrictions = checkRestrictions({
    conf,
    country,
    amount: toAmount,
    donorInfo,
    donationId: formDonationId,
  });

  let show = "";
  if (restrictions.length > 0) {
    show = "restrictions";
  } else if (numProfiles > 0 && !editDonorField && warnings.length === 0) {
    show = "selection-box";
  } else if (!!editDonorField || (numProfiles === 0 && warnings.length === 0)) {
    show = "editor-box";
  } else {
    show = "warnings-box";
  }

  const openInfoEditor = useCallback(() => {
    setErrors([]);
    setWarnings([]);
    setEditDonorField("pan");
  }, []);

  const ButtonPay = useCallback(
    ({ extraText, ...restProps }) => (
      <div>
        <Button sx={{ ...theme.buttons.variant1 }} {...restProps}>
          {" "}
          {loading && <Spinner size={16} sx={{ color: "text" }} />}
          Proceed to Pay <CurrencySymbol to={to} /> {toAmount} {extraText}
        </Button>
        <Tos country={country} sx={{ color: "text", fontWeight: "600" }} />
      </div>
    ),
    [to, toAmount, loading, country, theme.buttons.variant1]
  );

  const AfterSelection = useCallback(
    () => (
      <div sx={{ mt: 5 }}>
        {errors.length > 0 && (
          <div sx={theme.messages.variantEW}>
            <div sx={{ color: "errortext" }}>
              <Grid gap={1} columns={["1fr 7fr"]}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FontAwesomeIcon
                    icon="exclamation-circle"
                    sx={{ fontSize: "25px" }}
                  />
                </Box>
                <Box>
                  <Grid gap={0}>
                    <Box>
                      <h4 sx={{ mb: 0 }}>
                        <b>Form Errors.</b>
                      </h4>
                    </Box>
                    <Box>
                      <h5>Please fix to continue!</h5>
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            </div>
            <ErrorsOrWarnings errors={errors} warnings={warnings} />
          </div>
        )}
        {donorInfo.name && (
          <ButtonPay
            disabled={loading || errors.length > 0}
            onClick={validateAndSubmit}
          />
        )}
      </div>
    ),
    [
      errors,
      warnings,
      loading,
      validateAndSubmit,
      donorInfo,
      theme.messages.variantEW,
    ]
  );

  return (
    <Container sx={{ maxWidth: "320px", pb: 3, margin: "0 auto" }}>
      <div sx={{ mb: 3 }}>
        <AmountSelector
          country={country}
          currency={initialCurrency}
          initialAmount={initialAmount}
          initialCurrency={initialCurrency}
          setAmount={(x) => {
            setToAmount(x);
            setErrors([]);
            setWarnings([]);
          }}
          autoFocusName={editDonorField || "amount"}
        />
      </div>
      <RestrictionsWrapper
        show={show}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        restrictions={restrictions}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
      {show === "selection-box" && (
        <ProfileSelector
          containerStyle={containerStyle}
          country={country}
          initProfile={initialProfile}
          donorInfo={donorInfo}
          setDonorInfo={setDonorInfo}
          resetDonorInfo={resetDonorInfo}
          AfterSelection={AfterSelection}
          showOtherCitizen={showOtherCitizen}
          enableSignInOption={enableSignInOption}
          donationAmount={toAmount}
          meditationPlaceFieldAvl={meditationPlaceFieldAvl}
        />
      )}

      {show === "editor-box" && (
        <div>
          <div sx={{ color: "secondary", mb: 1 }}>Donor Information</div>
          <EditProfile
            country={country}
            ButtonPay={ButtonPay}
            value={editDonorField ? donorInfo : initialProfile}
            setValue={setDonorInfoAndTriggerPay}
            containerStyle={containerStyle}
            autoFocusName={editDonorField || "name"}
            showOtherCitizen={showOtherCitizen}
            enableSignInOption={enableSignInOption}
            donationAmount={toAmount}
            meditationPlaceFieldAvl={meditationPlaceFieldAvl}
          />
        </div>
      )}
      <ErrorsOrWarningsWrapper
        show={show}
        openInfoEditor={openInfoEditor}
        ButtonPay={ButtonPay}
        validateAndSubmitOW={validateAndSubmitOW}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        AfterSelection={AfterSelection}
        errors={errors}
        warnings={warnings}
        initialCurrency={initialCurrency}
        toAmount={toAmount}
        loading={loading}
        validateAndSubmit={validateAndSubmit}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
    </Container>
  );
};

OnlineWidget.propTypes = {
  formDonationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  initialAmount: PropTypes.number,
  initialCurrency: PropTypes.string,
  projectId: PropTypes.string,
  packageDescription: PropTypes.string,
  country: PropTypes.string,
  initialProfile: PropTypes.shape({}),
  paymentSuccessUrl: PropTypes.string.isRequired,
  paymentFailureUrl: PropTypes.string.isRequired,
  showOtherCitizen: PropTypes.bool,
  enableSignInOption: PropTypes.bool,
  meditationPlaceFieldAvl: PropTypes.bool,
};

OnlineWidget.defaultProps = {
  initialAmount: 0,
  initialCurrency: "INR",
  projectId: "",
  packageDescription: "",
  country: "in",
  initialProfile: {},
  showOtherCitizen: true,
  enableSignInOption: true,
  meditationPlaceFieldAvl: false,
};
export default OnlineWidget;
